/**
 * Several policies at once.
 *
 * Its own control, deliberately, rather than the single form with a
 * `multiple` flag. A batch needs things one file does not: a queue to review
 * before anything is sent, a title per row that a person can fix, a
 * per-row outcome afterwards, and a retry for the ones that failed. Bolting
 * those onto a form built around one title and one file would make both
 * forms worse. This one shares only what genuinely applies to every file —
 * the handling settings — through HandlingFields.
 *
 * The shape: choose or drop files → they appear as rows, each titled from
 * its filename and checked → fix any titles, remove any mistakes → Upload N
 * policies → watch the rows finish → retry the failures, or upload more.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { FileDrop } from '../../shared/FileDrop';
import { TITLE_MAX } from '../../types/fieldLimits';
import { ACCEPT, formatBytes, MAX_UPLOAD_BYTES } from './useUpload';
import { useBulkUpload, rowIsOpen, rowTitleEditable, MAX_BULK_FILES, type BulkRow, type RowStatus, type BulkSettings } from './useBulkUpload';
import { ApprovalFields, ReviewAndAcknowledgementFields } from './ReviewAndApproval';
import { TaxonomyFields, type Audience } from './TaxonomyFields';
import { useTaxonomy } from '../admin/useTaxonomy';
import { uploadedListUrl } from './library/libraryQuery';
import styles from '../../shared/Page.module.css';
import own from './UploadPage.module.css';
import bulk from './BulkUpload.module.css';

/** The status column, in words. */
export function describeStatus(status: RowStatus): string {
  switch (status.kind) {
    case 'checking':
      return 'Checking…';
    case 'ready':
      return 'Ready';
    case 'problem':
      return status.reason;
    case 'creating':
      return 'Creating the policy…';
    case 'uploading':
      return `Uploading… ${status.percent}%`;
    case 'attaching':
      return 'Attaching the document…';
    case 'converting':
      return 'Converting to PDF…';
    case 'submitting':
      return 'Submitting for approval…';
    case 'done':
      return status.note ? `Done, but: ${status.note}` : 'Done';
    case 'failed':
      return status.message;
  }
}

// CSS-module classes type as possibly undefined under strict settings.
const TONE: Record<RowStatus['kind'], string | undefined> = {
  checking: bulk.quiet,
  ready: bulk.ok,
  problem: bulk.bad,
  creating: bulk.busy,
  uploading: bulk.busy,
  attaching: bulk.busy,
  converting: bulk.busy,
  submitting: bulk.busy,
  done: bulk.ok,
  failed: bulk.bad,
};

export function BulkUploadPage() {
  const { session } = useAuth();
  const queue = useBulkUpload(session?.orgId ?? null);
  const { rows, running, finished, counts } = queue;
  const queueRef = useRef<HTMLDivElement>(null);

  const [reviewCycle, setReviewCycle] = useState(12);
  const [requiresAck, setRequiresAck] = useState(true);
  const [submitForReview, setSubmitForReview] = useState(true);

  /** What happened to files that did not become rows. */
  const [notice, setNotice] = useState<string | null>(null);

  /**
   * A row was sent back for a title: once the batch has stopped and the row
   * is open again, put the cursor on it. Done after the render, because at
   * the moment start() resolves the rows are still drawn as locked.
   */
  const [focusRequest, setFocusRequest] = useState(0);
  const focusedFor = useRef(0);
  useEffect(() => {
    if (focusRequest === focusedFor.current || running) return;
    focusedFor.current = focusRequest;
    queueRef.current?.querySelector<HTMLInputElement>('input[aria-invalid="true"]')?.focus();
  }, [focusRequest, running]);

  /** Set when Upload is pressed with no document type chosen: every new policy needs one. */
  const [typeAsked, setTypeAsked] = useState(false);
  const [departmentId, setDepartmentId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [documentTypeId, setDocumentTypeId] = useState('');
  const [audience, setAudience] = useState<Audience>('all');
  /** The same rule as the single upload: no department, no department audience. */
  const chooseDepartment = (id: string) => {
    setDepartmentId(id);
    if (!id) setAudience('all');
  };
  const departments = useTaxonomy('department', Boolean(session?.orgId));
  const categories = useTaxonomy('category', Boolean(session?.orgId));
  const docTypes = useTaxonomy('documentType', Boolean(session?.orgId));

  const settings: BulkSettings = {
    reviewCycleMonths: reviewCycle,
    requiresAcknowledgement: requiresAck,
    submitForReview,
    ...(departmentId ? { departmentIds: [departmentId] } : {}),
    ...(categoryId ? { categoryId } : {}),
    ...(documentTypeId ? { documentTypeId } : {}),
    targetAudience: departmentId && audience === 'departments' ? 'departments' : 'all',
  };

  const uploadable = counts.ready;
  /** Any row a person may still remove — so the column exists. */
  const removable = !running && rows.some(rowIsOpen);

  function onFiles(files: File[]) {
    const { duplicates, overCap } = queue.addFiles(files);
    const parts: string[] = [];
    if (duplicates) parts.push(`${duplicates} ${duplicates === 1 ? 'was' : 'were'} already in the list`);
    if (overCap) parts.push(`${overCap} left out — a batch holds at most ${MAX_BULK_FILES} files`);
    setNotice(parts.length ? parts.join('; ') + '.' : null);
  }

  async function upload() {
    const { needsTitle } = await queue.start(settings);
    if (needsTitle) setFocusRequest((n) => n + 1);
  }

  /** Rows that will never upload: wrong type, too big, unreadable. */
  const invalid = counts.problems - counts.needsTitle;

  // "View policies" shows this batch and nothing else: the list, loaded with
  // the scope these landed in, narrowed to their ids. A person who uploaded
  // twelve expects to see twelve, not the whole library.
  const doneIds = rows.flatMap((r) => (r.status.kind === 'done' ? [r.status.policyId] : []));
  const viewUrl = finished ? uploadedListUrl(doneIds) : '/policies';

  /** Before a batch runs: what is in the list that will not upload as is. */
  function pending(): string {
    const parts: string[] = [];
    const broken = invalid;
    if (counts.needsTitle) parts.push(`${counts.needsTitle} ${counts.needsTitle === 1 ? 'file needs' : 'files need'} a title`);
    if (broken) parts.push(`${broken} ${broken === 1 ? 'file has' : 'files have'} a problem and will be skipped`);
    return parts.length ? parts.join('; ') + '.' : '';
  }

  return (
    <div className={own.form}>
      <div className={own.field}>
        <label className={own.label} htmlFor="bulk-files">
          Documents
        </label>
        <FileDrop
          id="bulk-files"
          multiple
          onFiles={onFiles}
          accept={ACCEPT}
          disabled={running || finished}
          prompt="Choose PDFs or Word documents"
          describedBy="bulk-files-hint"
        />
        <p className={own.hint} id="bulk-files-hint">
          PDF or Word (.docx), up to {formatBytes(MAX_UPLOAD_BYTES)} each, {MAX_BULK_FILES} at a
          time. A Word file is kept as the original and converted to PDF for reading. Each
          file becomes one policy, named from its file name — check the names below before
          uploading.
        </p>
        {notice && (
          <p className={own.hint} role="status">
            {notice}
          </p>
        )}
      </div>

      {rows.length > 0 && (
        <div className={bulk.queue} ref={queueRef}>
          <div className={bulk.queueHead}>
            <p className={own.label} id="bulk-queue-label">
              {rows.length} {rows.length === 1 ? 'file' : 'files'}
            </p>
            {!running && !finished && (
              <div className={bulk.queueTools}>
                {invalid > 0 && (
                  <button type="button" className={bulk.linkBtn} onClick={queue.removeInvalid}>
                    Clear {invalid === 1 ? 'the invalid file' : `${invalid} invalid files`}
                  </button>
                )}
                <button type="button" className={bulk.linkBtn} onClick={queue.reset}>
                  Clear the list
                </button>
              </div>
            )}
          </div>

          <div className={bulk.scroll}>
            <table className={bulk.table} aria-labelledby="bulk-queue-label">
              <thead>
                <tr>
                  <th scope="col">Title</th>
                  <th scope="col">File</th>
                  <th scope="col">Status</th>
                  {removable && (
                    <th scope="col">
                      <span className={bulk.srOnly}>Remove</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Row
                    key={row.id}
                    row={row}
                    running={running}
                    removeColumn={removable}
                    onTitle={(t) => queue.setTitle(row.id, t)}
                    onRemove={() => queue.removeRow(row.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <TaxonomyFields
        idPrefix="bulk"
        plural
        departments={departments.data}
        categories={categories.data}
        docTypes={docTypes.data}
        departmentId={departmentId}
        onDepartmentId={chooseDepartment}
        categoryId={categoryId}
        onCategoryId={setCategoryId}
        documentTypeId={documentTypeId}
        onDocumentTypeId={setDocumentTypeId}
        documentTypeError={typeAsked && !documentTypeId ? 'Choose a document type for these policies.' : null}
        disabled={running || finished}
      />

      {/* The same controls in the same order as the single upload, which is
          the whole reason they are shared components. */}
      <ReviewAndAcknowledgementFields
        idPrefix="bulk"
        plural
        reviewCycle={reviewCycle}
        onReviewCycle={setReviewCycle}
        requiresAck={requiresAck}
        onRequiresAck={setRequiresAck}
        audience={audience}
        onAudience={setAudience}
        departmentId={departmentId}
        departmentName={departments.data?.find((d) => d.id === departmentId)?.name}
        disabled={running || finished}
      />

      <ApprovalFields
        plural
        submitForReview={submitForReview}
        onSubmitForReview={setSubmitForReview}
        disabled={running || finished}
      />

      {/* Said aloud as it changes: a person who cannot see twelve rows
          filling in still hears "9 of 12 uploaded". */}
      <p className={bulk.summary} role="status" aria-live="polite">
        {running && `${counts.done + counts.failed} of ${counts.done + counts.failed + counts.inFlight + counts.ready} uploaded…`}
        {finished &&
          `${counts.done} uploaded` +
            (counts.failed ? `, ${counts.failed} failed` : '') +
            (counts.problems ? `, ${counts.problems} skipped` : '') +
            '.'}
        {!running && !finished && pending()}
      </p>

      <div className={styles.actions}>
        {(!finished || uploadable > 0) && (
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={running || uploadable === 0}
            onClick={() => { if (!documentTypeId) setTypeAsked(true); else void upload(); }}
          >
            {running
              ? 'Uploading…'
              : `Upload ${uploadable} ${uploadable === 1 ? 'policy' : 'policies'}`}
          </button>
        )}
        {finished && counts.failed > 0 && (
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={() => void queue.retryFailed(settings)}
          >
            Retry {counts.failed} failed
          </button>
        )}
        {finished && (
          <button type="button" className={styles.btn} onClick={queue.reset}>
            Upload more
          </button>
        )}
        <Link to={viewUrl} className={styles.btn}>
          {finished ? 'View policies' : 'Cancel'}
        </Link>
      </div>
    </div>
  );
}

function Row({
  row,
  running,
  removeColumn,
  onTitle,
  onRemove,
}: {
  row: BulkRow;
  running: boolean;
  /** The table has a Remove column; a settled row leaves its cell empty. */
  removeColumn: boolean;
  onTitle: (title: string) => void;
  onRemove: () => void;
}) {
  const status = row.status;
  const titleId = `bulk-title-${row.id.replace(/[^A-Za-z0-9_-]/g, '_')}`;
  const editable = !running && rowTitleEditable(row);

  return (
    <tr className={status.kind === 'problem' || status.kind === 'failed' ? bulk.rowBad : undefined}>
      <td className={bulk.titleCell}>
        {editable ? (
          <input
            id={titleId}
            className={bulk.titleInput}
            value={row.title}
            maxLength={TITLE_MAX}
            onChange={(e) => onTitle(e.target.value.slice(0, TITLE_MAX))}
            aria-label={`Title for ${row.file.name}`}
            aria-invalid={status.kind === 'problem' && !row.title.trim() ? true : undefined}
          />
        ) : (
          <span className={bulk.titleText}>
            {status.kind === 'done' ? (
              <Link to={`/policy/${status.policyId}`}>{row.title}</Link>
            ) : (
              row.title || <em className={bulk.quiet}>untitled</em>
            )}
          </span>
        )}
      </td>
      <td className={bulk.fileCell}>
        <span className={bulk.fileName}>{row.file.name}</span>
        <span className={bulk.fileSize}>{formatBytes(row.file.size)}</span>
      </td>
      <td className={`${bulk.statusCell} ${TONE[status.kind] ?? ''}`}>
        {describeStatus(status)}
        {status.kind === 'uploading' && (
          <div
            className={bulk.track}
            role="progressbar"
            aria-valuenow={status.percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Upload progress for ${row.file.name}`}
          >
            <div className={bulk.fill} style={{ width: `${status.percent}%` }} />
          </div>
        )}
      </td>
      {removeColumn && (
        <td className={bulk.removeCell}>
          {!running && rowIsOpen(row) && (
            <button
              type="button"
              className={bulk.remove}
              onClick={onRemove}
              aria-label={`Remove ${row.file.name}`}
            >
              <span aria-hidden="true">×</span>
            </button>
          )}
        </td>
      )}
    </tr>
  );
}
